import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  searchUserByFieldAsync,
  selectSearchedUser,
  selectIsSearching,
  fetchUserActivitiesAsync,
  selectUserActivities,
} from "@/store/redux/slices/user/user_slice";
import {
  deleteUserAsync,
  selectFetchedUsers,
} from "@/store/redux/slices/user_admin/user_admin";
import Back from "@/components/specific/Back";
import Breadcrumb from "@/components/specific/Breadcrumb";
import UserProfileHeader from "@/pages/user_management/components/single_user/User_Profile_Header";
import ActivityTimeline from "@/pages/user_management/components/single_user/Activity_Timeline";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import InviteUserFormModal from "@/pages/user_management/components/Invite_User_Form_Modal";
import { useLoader } from "@/hooks/LoaderProvider";
import { resolveRoleName } from "@/utils/organization_id";
import { getTimeAgo } from "@/utils/date_time_year_utils";

const SingleUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector(selectSearchedUser);
  const isLoading = useSelector(selectIsSearching);
  const rawActivities = useSelector(selectUserActivities);
  const fetchedUsers = useSelector(selectFetchedUsers);
  const listUsers = useSelector((state) => state.userAdmin?.users || []);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  // Prefer org-list cache (roles usually already populated) over by-id search.
  const allUsers = useMemo(
    () => (fetchedUsers?.length > 0 ? fetchedUsers : listUsers),
    [fetchedUsers, listUsers]
  );
  const cachedUser = useMemo(
    () => allUsers.find((u) => u._id === id || u.id === id),
    [allUsers, id]
  );

  useEffect(() => {
    if (cachedUser) return;

    const fetchUser = async () => {
      if (!id) return;
      showLoader();
      try {
        await dispatch(
          searchUserByFieldAsync({
            key: "_id",
            value: id,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error fetching user:", error);
      } finally {
        hideLoader();
      }
    };

    fetchUser();
  }, [dispatch, hideLoader, id, showLoader, cachedUser]);

  const user = cachedUser || userData?.data?.[0] || null;

  // Temporary: inspect user payload so we can map the correct party field.
  useEffect(() => {
    if (!user) return;
    console.log("[SingleUser] user response for party mapping:", user);
    console.log("[SingleUser] searchedUser payload:", userData);
  }, [user, userData]);

  useEffect(() => {
    const userId = user?._id || user?.id;
    if (!userId) return;
    dispatch(fetchUserActivitiesAsync(userId));
  }, [dispatch, user?._id, user?.id]);

  // Map GET /v1/user/{id}/activity into the existing CMS timeline shape.
  const userActivities = useMemo(
    () =>
      (rawActivities || []).map((item, index) => ({
        id: item._id || item.id || `activity-${index}`,
        time: getTimeAgo(item.createdAt),
        task:
          item.details ||
          `${item.action || ""} ${item.entityType || ""}`.trim() ||
          "Activity",
        status: item.entityType || item.action || "",
        assignedBy: user?.fullname || user?.name || "System",
      })),
    [rawActivities, user?.fullname, user?.name]
  );

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    showLoader();
    try {
      await dispatch(deleteUserAsync(id)).unwrap();
      hideLoader();
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error deleting user:", error);
      hideLoader();
    }
  };

  const handleModify = () => {
    setIsEditModalVisible(true);
  };

  const handleModalClose = async () => {
    setIsEditModalVisible(false);
    showLoader();
    try {
      await dispatch(
        searchUserByFieldAsync({
          key: "_id",
          value: id,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error refreshing user data:", error);
    } finally {
      hideLoader();
    }
  };

  if (isLoading && !cachedUser) {
    return null;
  }

  if (!user) {
    return <div>User not found</div>;
  }

  const primaryRole = resolveRoleName(user.roles) || "User";

  const transformedUserData = {
    id: user.id,
    name: user?.fullname,
    role: primaryRole,
    email: user?.email,
    phone: user.phone,
    status: user?.status,
    createdAt: user?.createdAt,
    avatar: user?.profileImage,
    // Keep current fallback until party path is confirmed from console log.
    party: user.party || "Not Affiliated",
  };

  const editUserData = {
    id: user.id,
    _id: user._id,
    name: user.fullname,
    fullname: user.fullname,
    email: user.email,
    phone: user.phone,
    roles: user.roles,
    role: primaryRole,
    party: user.party,
  };

  const items = [
    { title: "Team Admin", link: "dashboard/users" },
    { title: `${user.fullname}`, link: "" },
  ];
  return (
    <div className="w-full">
      <Breadcrumb items={items} />
      <Back />
      <div className="mt-6">
        <UserProfileHeader
          user={transformedUserData}
          onDelete={handleDelete}
          onModify={handleModify}
        />
        <ActivityTimeline
          avatar={transformedUserData.avatar}
          activities={userActivities}
        />
      </div>

      <Delete
        isVisible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onProceed={handleDeleteConfirm}
        header="Delete User"
        paragraph={`Are you sure you want to delete "${transformedUserData.name}"?`}
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />

      <Success
        isVisible={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate("/dashboard/users");
        }}
        header="User Deleted"
        paragraph="The user has been successfully deleted"
        buttonText="Continue"
      />

      <InviteUserFormModal
        isVisible={isEditModalVisible}
        onClose={handleModalClose}
        onUserCreated={handleModalClose}
        userData={editUserData}
        isEditMode={true}
      />
    </div>
  );
};

export default SingleUser;
